import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Spinner from '../../components/Spinner';

const fmtData = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';

function labelPratica(richiesta) {
  const t = (richiesta || '').toLowerCase();
  if (t.includes('revis')) return 'Verifica triennale';
  return richiesta || '—';
}

function badgeClass(richiesta) {
  const t = (richiesta || '').toLowerCase();
  if (t.includes('iscriz')) return 'bg-blue-100 text-blue-700';
  if (t.includes('revis') || t.includes('verif') || t.includes('triennal')) return 'bg-green-100 text-green-700';
  if (t.includes('rinnov')) return 'bg-purple-100 text-purple-700';
  return 'bg-gray-100 text-gray-600';
}

export default function GruppoImpresa() {
  const { nome } = useParams();
  const nomeDecoded = decodeURIComponent(nome);
  const navigate = useNavigate();
  const [pratiche, setPratiche] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/api/imprese')
      .then(r => {
        const filtrate = r.data.filter(
          i => i.ragione_sociale.trim().toLowerCase() === nomeDecoded.trim().toLowerCase()
        );
        // Ordina per richiesta e numero_richiesta
        filtrate.sort((a, b) => {
          const ra = (a.richiesta || '').toLowerCase();
          const rb = (b.richiesta || '').toLowerCase();
          if (ra !== rb) return ra.localeCompare(rb);
          return parseInt(a.numero_richiesta || 0) - parseInt(b.numero_richiesta || 0);
        });
        setPratiche(filtrate);
      })
      .finally(() => setLoading(false));
  }, [nomeDecoded]);

  if (loading) return <Spinner />;
  if (pratiche.length === 0) return <p className="text-red-600">Impresa non trovata</p>;

  // Prendi i dati anagrafici dalla prima pratica disponibile
  const base = pratiche.find(p =>
    (p.richiesta || '').toLowerCase().includes('iscriz') && p.numero_richiesta === '1'
  ) || pratiche[0];

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <button onClick={() => navigate('/admin/imprese')} className="text-[#2E6096] hover:underline text-sm">
            ← Imprese
          </button>
          <h1 className="text-2xl font-bold text-[#1A3A5C] mt-1">{base.ragione_sociale}</h1>
          <p className="text-gray-500 text-sm">
            {base.citta}{base.provincia ? ` (${base.provincia})` : ''}{base.partita_iva ? ` — P.IVA ${base.partita_iva}` : ''}
          </p>
        </div>
        <button
          onClick={() => navigate('/admin/imprese/nuova', { state: { prefill: base } })}
          className="bg-[#1A3A5C] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#2E6096] transition"
        >
          + Nuova pratica
        </button>
      </div>

      {/* Riepilogo anagrafica */}
      <div className="bg-white rounded-xl shadow p-5 mb-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">Indirizzo</p>
          <p className="text-gray-700">
            {base.indirizzo || '—'}
            {(base.cap || base.citta || base.provincia) && (
              <span className="block text-gray-500">
                {[base.cap, base.citta, base.provincia ? `(${base.provincia})` : null].filter(Boolean).join(' ')}
              </span>
            )}
          </p>
        </div>
        <div>
          <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">P.IVA</p>
          <p className="text-gray-700">{base.partita_iva || '—'}</p>
        </div>
        <div>
          <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">Cod. Fiscale</p>
          <p className="text-gray-700">{base.codice_fiscale || '—'}</p>
        </div>
        <div>
          <p className="text-gray-400 text-xs uppercase tracking-wide mb-1">Email</p>
          <p className="text-gray-700">{base.email || '—'}</p>
        </div>
      </div>

      {/* Pratiche */}
      <h2 className="text-lg font-semibold text-[#1A3A5C] mb-3">
        {pratiche.length} {pratiche.length === 1 ? 'pratica' : 'pratiche'}
      </h2>
      <div className="bg-white rounded-xl shadow overflow-hidden">
        {/* Card view — mobile */}
        <div className="sm:hidden divide-y divide-gray-100">
          {pratiche.map(p => (
            <div key={p.id} className="p-4 active:bg-[#EEF4FB]" onClick={() => navigate(`/admin/imprese/${p.id}`)}>
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${badgeClass(p.richiesta)}`}>
                  {labelPratica(p.richiesta)} {p.numero_richiesta || ''}
                </span>
                <span className="text-[#2E6096] text-sm font-medium">Apri →</span>
              </div>
              {p.soa && <p className="text-sm text-gray-700 mt-1">{p.soa}</p>}
              <p className="text-xs text-gray-500 mt-0.5">
                {p.data_attestazione ? `Attestazione: ${fmtData(p.data_attestazione)}` : ''}
                {p.data_contratto ? ` · Contratto: ${fmtData(p.data_contratto)}` : ''}
              </p>
            </div>
          ))}
        </div>
        {/* Tabella — desktop */}
        <table className="hidden sm:table w-full text-sm">
          <thead className="bg-[#EEF4FB] text-[#1A3A5C] font-semibold">
            <tr>
              <th className="text-left px-4 py-3">Tipo pratica</th>
              <th className="text-left px-4 py-3">N.</th>
              <th className="text-left px-4 py-3">Attestazione SOA</th>
              <th className="text-left px-4 py-3">Data attestazione</th>
              <th className="text-left px-4 py-3">Data contratto</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {pratiche.map((p, i) => (
              <tr key={p.id}
                className={`${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-[#EEF4FB] cursor-pointer transition`}
                onClick={() => navigate(`/admin/imprese/${p.id}`)}>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${badgeClass(p.richiesta)}`}>
                    {labelPratica(p.richiesta)}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-600">{p.numero_richiesta || '—'}</td>
                <td className="px-4 py-3 text-gray-700">{p.soa || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{fmtData(p.data_attestazione)}</td>
                <td className="px-4 py-3 text-gray-600">{fmtData(p.data_contratto)}</td>
                <td className="px-4 py-3 text-right">
                  <span className="text-[#2E6096] font-medium">Apri →</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
